import React from 'react';
import { Link } from 'react-router-dom';

export default function Footer({ variant }) {
  const currentYear = new Date().getFullYear();
  const isDashboard = variant === 'dashboard';

  const footerBg = isDashboard
    ? 'bg-[#0a0f1e] border-t border-white/5'
    : 'bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700';

  const headingColor = isDashboard
    ? 'text-white'
    : 'text-gray-900 dark:text-white';

  const linkColor = isDashboard
    ? 'text-gray-500 hover:text-emerald-400'
    : 'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-white';

  const bodyText = isDashboard
    ? 'text-gray-500'
    : 'text-gray-600 dark:text-gray-400';

  const brandText = isDashboard
    ? 'text-white'
    : 'text-gray-900 dark:text-white';

  const dividerColor = isDashboard
    ? 'border-white/5'
    : 'border-gray-200 dark:border-gray-700';

  const badgeText = isDashboard
    ? 'text-gray-600'
    : 'text-gray-600 dark:text-gray-400';

  const subtleText = isDashboard
    ? 'text-gray-700'
    : 'text-gray-500 dark:text-gray-500';

  return (
    <footer className={`w-full ${footerBg} mt-auto`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-8">
          {/* Brand Section */}
          <div className="col-span-1 sm:col-span-2 lg:col-span-1">
            <Link to="/" className="flex items-center space-x-2 mb-3 sm:mb-4">
              <img src="/logo-icon.png" alt="SiteScope" className="w-7 h-7 sm:w-8 sm:h-8" />
              <span className={`text-lg sm:text-xl font-bold ${brandText}`}>SiteScope</span>
            </Link>
            <p className={`text-xs sm:text-sm ${bodyText} leading-relaxed`}>
              Website monitoring for freelancers and agencies — works with any site.
            </p>
          </div>

          {/* Company Links */}
          <div>
            <h3 className={`text-xs sm:text-sm font-semibold ${headingColor} uppercase tracking-wider mb-3 sm:mb-4`}>
              Company
            </h3>
            <ul className="space-y-2">
              <li><Link to="/" className={`text-xs sm:text-sm ${linkColor} transition-colors`}>Home</Link></li>
              <li><Link to="/why-sitescope" className={`text-xs sm:text-sm ${linkColor} transition-colors`}>Why SiteScope</Link></li>
              <li><Link to="/pricing" className={`text-xs sm:text-sm ${linkColor} transition-colors`}>Pricing</Link></li>
              <li><Link to="/blog" className={`text-xs sm:text-sm ${linkColor} transition-colors`}>Blog</Link></li>
            </ul>
          </div>

          {/* Legal Links */}
          <div>
            <h3 className={`text-xs sm:text-sm font-semibold ${headingColor} uppercase tracking-wider mb-3 sm:mb-4`}>
              Legal
            </h3>
            <ul className="space-y-2">
              <li><Link to="/faq" className={`text-xs sm:text-sm ${linkColor} transition-colors`}>FAQ</Link></li>
              <li><Link to="/refund-policy" className={`text-xs sm:text-sm ${linkColor} transition-colors`}>Refund Policy</Link></li>
              <li><Link to="/terms" className={`text-xs sm:text-sm ${linkColor} transition-colors`}>Terms of Service</Link></li>
              <li><Link to="/privacy" className={`text-xs sm:text-sm ${linkColor} transition-colors`}>Privacy Policy</Link></li>
              <li>
                <button 
                  onClick={() => {
                    localStorage.removeItem('cookieConsent');
                    localStorage.removeItem('cookieConsentDate');
                    window.location.reload();
                  }}
                  className={`text-xs sm:text-sm ${linkColor} transition-colors text-left`}
                >
                  Cookie Settings
                </button>
              </li>
            </ul>
          </div>

          {/* Contact & Support */}
          <div>
            <h3 className={`text-xs sm:text-sm font-semibold ${headingColor} uppercase tracking-wider mb-3 sm:mb-4`}>
              Support
            </h3>
            <ul className="space-y-2">
              <li><a href="mailto:support@sitescope.com" className={`text-xs sm:text-sm ${linkColor} transition-colors break-all`}>support@sitescope.com</a></li>
              <li><a href="mailto:privacy@sitescope.com" className={`text-xs sm:text-sm ${linkColor} transition-colors`}>Privacy Requests</a></li>
              <li><Link to="/settings" className={`text-xs sm:text-sm ${linkColor} transition-colors`}>Account Settings</Link></li>
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className={`mt-6 sm:mt-8 pt-4 sm:pt-6 border-t ${dividerColor}`}>
          {/* Trust Badges */}
          <div className={`flex flex-wrap items-center justify-center gap-4 sm:gap-6 mb-4 sm:mb-6 pb-4 sm:pb-6 border-b ${dividerColor}`}>
            <div className={`flex items-center gap-2 text-xs sm:text-sm ${badgeText}`}>
              <span className="text-lg">💯</span><span>30-Day Money Back</span>
            </div>
            <div className={`flex items-center gap-2 text-xs sm:text-sm ${badgeText}`}>
              <span className="text-lg">🔒</span><span>Secure Payments</span>
            </div>
            <div className={`flex items-center gap-2 text-xs sm:text-sm ${badgeText}`}>
              <span className="text-lg">🛡️</span><span>Privacy Protected</span>
            </div>
            <div className={`flex items-center gap-2 text-xs sm:text-sm ${badgeText}`}>
              <span className="text-lg">⚡</span><span>Free: 3 client sites</span>
            </div>
          </div>
          
          <div className="flex flex-col sm:flex-row justify-between items-center space-y-3 sm:space-y-0 gap-3">
            <p className={`text-xs sm:text-sm ${bodyText} text-center sm:text-left`}>
              &copy; {currentYear} SiteScope. All rights reserved.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-6">
              <span className={`text-xs ${subtleText} whitespace-nowrap`}>GDPR Compliant</span>
              <span className={`text-xs ${subtleText} whitespace-nowrap`}>CCPA Compliant</span>
              <span className={`text-xs ${subtleText} whitespace-nowrap`}>SSL Secured</span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
